import React, { useEffect, useMemo, useRef } from 'react';
import BusinessIcon from '@mui/icons-material/Business';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useLocation, useNavigate } from 'react-router-dom';
import { CrmButton, CrmTabs } from '../components/ui';
import CrmListPage from '../components/listing/CrmListPage';
import CommercialContextSettings from './CommercialContextSettings';
import CheckoutArchitectureSettings from './CheckoutArchitectureSettings';
import CheckoutMapSettings from './CheckoutMapSettings';
import CommercePolicySettings from './CommercePolicySettings';
import Settings from './Settings';
import CommunicationSettings from './CommunicationSettings';
import OperationalFlowSettings from './OperationalFlowSettings';
import StorefrontAuthSettings from './StorefrontAuthSettings';
import './settings-hub.scss';

const ORDER_FULFILMENT_ITEMS = [
  { key: 'fulfilmentSettings', label: 'Fulfilment Availability', description: 'Storefront/commerce availability for delivery, pickup and shipping' },
  { key: 'fulfilmentProfiles', label: 'Fulfilment Profiles', description: 'Operational flows, release rules and allowed fulfilment methods' },
  { key: 'deliveryZones', label: 'Delivery Zones', description: 'Service areas and delivery geography' },
  { key: 'deliveryServices', label: 'Delivery / Shipping Methods', description: 'Named delivery and shipping methods available by channel' },
  { key: 'deliveryPolicies', label: 'Delivery Policies', description: 'Charges, eligibility, lead time and capacity rules' },
  { key: 'slotProfiles', label: 'Delivery Slots', description: 'Windows, lead time, capacity and calendars' },
  { key: 'pickupLocations', label: 'Pickup Locations', description: 'Pickup points and operating instructions' },
  { key: 'pickupPolicies', label: 'Pickup Policies', description: 'Pickup charges, eligibility and timing rules' },
];

const PROMOTIONS_ITEMS = [
  { key: 'promotions', label: 'Promotions', description: 'Automatic promotions and eligibility rules' },
  { key: 'stackingPolicies', label: 'Promotion Stacking', description: 'Rules for combining coupons, offers and credits' },
  { key: 'coupons', label: 'Coupons', description: 'Scoped coupons and customer eligibility' },
  { key: 'referralPrograms', label: 'Referral Programs', description: 'Referral rewards and qualification rules' },
  { key: 'customerSegments', label: 'Customer Segments', description: 'Segment definitions used by pricing and policies' },
];

const POST_ORDER_ITEMS = [
  { key: 'cancellationPolicies', label: 'Cancellation Policies', description: 'Customer/staff cancellation rules and refund links' },
  { key: 'refundPolicies', label: 'Refund Policies', description: 'Refund eligibility, approvals and reversals' },
  { key: 'orderModificationPolicies', label: 'Order Modifications', description: 'Allowed order changes, approvals and repricing rules' },
];

const sections = [
  {
    heading: 'Account setup',
    items: [
      { key: 'preferences', label: 'Display Preferences', description: 'Dashboard currency and date format', icon: <SettingsSuggestIcon /> },
      { key: 'structure', label: 'Business Structure', description: 'Legal entities, brands, business units, channels and storefronts', icon: <BusinessIcon /> },
      { key: 'taxation', label: 'Tax & Legal Identity', description: 'GSTIN, PAN and legal addresses used on tax documents', icon: <BusinessIcon /> },
      { key: 'documentTemplates', label: 'Document Templates', description: 'Edit quotation and invoice document templates', icon: <DescriptionOutlinedIcon /> },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { key: 'orderFulfilment', label: 'Order Fulfilment', description: 'Operational flows, delivery, pickup, zones, slots and policies', icon: <LocalShippingIcon /> },
      { key: 'promotionsOffers', label: 'Promotions & Offers', description: 'Promotions, coupons and referrals', icon: <ShoppingCartIcon /> },
      { key: 'postOrderPolicies', label: 'Post-order Policies', description: 'Cancellations, refunds and order modifications', icon: <AccountTreeOutlinedIcon /> },
      { key: 'checkoutPayments', label: 'Checkout & Payments', description: 'Checkout profiles and payment gateway configuration', icon: <ShoppingCartIcon /> },
      { key: 'checkoutMaps', label: 'Maps & Address', description: 'Address search, map pins and reverse-geocoding configuration', icon: <LocalShippingIcon /> },
      { key: 'operationalFlows', label: 'Operational Flows', description: 'Visualize release conditions, fulfilment actions, invoice generation and communication triggers', icon: <AccountTreeOutlinedIcon /> },
      { key: 'communications', label: 'Notifications & Communications', description: 'Brevo, Notifino, templates, routing, triggers, reminders and delivery logs', icon: <EmailOutlinedIcon /> },
    ],
  },
  {
    heading: 'Customer identity',
    items: [
      { key: 'storefrontAccounts', label: 'Storefront Customer Accounts', description: 'Registration, login, guest checkout, verification and customer account capabilities', icon: <PersonOutlineIcon /> },
    ],
  },
];

const GROUPS = {
  orderFulfilment: {
    label: 'Order Fulfilment',
    description: 'Configure operational flows, fulfilment availability, delivery, pickup, zones, slots and related rules.',
    items: ORDER_FULFILMENT_ITEMS,
  },
  promotionsOffers: {
    label: 'Promotions & Offers',
    description: 'Configure promotions, coupons, referrals and customer targeting.',
    items: PROMOTIONS_ITEMS,
  },
  postOrderPolicies: {
    label: 'Post-order Policies',
    description: 'Configure cancellation, refund and order-modification rules.',
    items: POST_ORDER_ITEMS,
  },
};

const ITEM_TO_GROUP = Object.fromEntries(
  Object.entries(GROUPS).flatMap(([groupKey, group]) => group.items.map((item) => [item.key, groupKey]))
);

const ITEM_TO_SIDEBAR = { checkout: 'checkoutPayments', gateways: 'checkoutPayments' };
const ALL_ITEMS = sections.flatMap((section) => section.items);

export default function SettingsHub() {
  const location = useLocation();
  const navigate = useNavigate();
  const contentRef = useRef(null);
  const searchParams = new URLSearchParams(location.search);
  const requestedSection = searchParams.get('section') || 'preferences';
  const requestedEntity = searchParams.get('entity') || null;
  const activeGroupKey = ITEM_TO_GROUP[requestedSection] || (GROUPS[requestedSection] ? requestedSection : null);
  const active = activeGroupKey
    ? (GROUPS[activeGroupKey].items.some((item) => item.key === requestedSection)
      ? requestedSection
      : GROUPS[activeGroupKey].items[0].key)
    : requestedSection;

  const current = useMemo(() => {
    if (activeGroupKey) return { key: activeGroupKey, ...GROUPS[activeGroupKey] };
    const sidebarOwner = ITEM_TO_SIDEBAR[active];
    if (sidebarOwner) return ALL_ITEMS.find((item) => item.key === sidebarOwner) || ALL_ITEMS[0];
    return ALL_ITEMS.find((item) => item.key === active) || ALL_ITEMS[0];
  }, [active, activeGroupKey]);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [active]);

  const navigateToSection = (section, extra = {}) => {
    const next = new URLSearchParams();
    next.set('section', section);
    Object.entries(extra).forEach(([key, value]) => {
      if (value != null && value !== '') next.set(key, value);
    });
    navigate(`/settings?${next.toString()}`);
  };

  const select = (key) => {
    if (GROUPS[key]) return navigateToSection(GROUPS[key].items[0].key);
    if (key === 'checkoutPayments') return navigateToSection('checkout');
    navigateToSection(key);
  };

  const activeGroup = activeGroupKey ? GROUPS[activeGroupKey] : null;
  const groupedTabs = activeGroup ? (
    <CrmTabs
      className="settings-hub__tabs"
      items={activeGroup.items.map((item) => ({ id: item.key, label: item.label }))}
      activeId={active}
      onChange={(key) => navigateToSection(key)}
      ariaLabel={`${activeGroup.label} settings`}
    />
  ) : null;

  const content = active === 'preferences'
    ? <Settings embedded />
    : active === 'documentTemplates'
      ? (
        <div className="settings-hub__document-links">
          <div className="settings-hub__document-link">
            <div><strong>Quotation templates</strong><span>Edit quotation layout, content, payment terms, branding and PDF presentation.</span></div>
            <CrmButton variant="secondary" onClick={() => navigate('/quotations-settings')}>Edit quotation templates</CrmButton>
          </div>
          <div className="settings-hub__document-link">
            <div><strong>Invoice templates</strong><span>Edit invoice layout, payment instructions, branding and PDF presentation.</span></div>
            <CrmButton variant="secondary" onClick={() => navigate('/invoice-settings')}>Edit invoice templates</CrmButton>
          </div>
        </div>
      )
    : active === 'structure' || active === 'taxation'
      ? <CommercialContextSettings embedded initialEntity={requestedEntity || 'legal-entities'} taxOnly={active === 'taxation'} />
        : active === 'checkoutMaps'
          ? <CheckoutMapSettings />
          : active === 'checkout' || active === 'gateways'
        ? (
          <CheckoutArchitectureSettings
            initialTab={active === 'gateways' ? 1 : 0}
            embedded
            onTabChange={(tabId) => navigateToSection(tabId === 'gateways' ? 'gateways' : 'checkout')}
          />
        )
        : active === 'communications'
          ? <CommunicationSettings />
          : active === 'operationalFlows'
            ? <OperationalFlowSettings />
            : active === 'storefrontAccounts'
              ? <StorefrontAuthSettings />
              : <CommercePolicySettings entity={active} />;

  return (
    <CrmListPage
      title="Settings"
      subtitle="Manage how JDH sells, fulfils and records business."
      hideSavedViews
      hideToolbar
      hideFooter
    >
      <div className="settings-hub">
        <aside className="settings-hub__sidebar" aria-label="Settings navigation">
          {sections.map((section) => (
            <section className="settings-hub__nav-section" key={section.heading}>
              <h2>{section.heading}</h2>
              <div className="settings-hub__nav-list">
                {section.items.map((item) => {
                  const selected = current.key === item.key;
                  return (
                    <button
                      type="button"
                      key={item.key}
                      className={`settings-hub__nav-item ${selected ? 'is-active' : ''}`}
                      onClick={() => select(item.key)}
                    >
                      <span className="settings-hub__nav-icon" aria-hidden="true">{item.icon}</span>
                      <span className="settings-hub__nav-copy">
                        <strong>{item.label}</strong>
                        <small>{item.description}</small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </aside>

        <main className="settings-hub__main" ref={contentRef}>
          <div className="settings-hub__panel">
            {groupedTabs}
            <div className="settings-hub__panel-content">{content}</div>
          </div>
        </main>
      </div>
    </CrmListPage>
  );
}
